import { useEffect, useLayoutEffect, useMemo, useRef } from 'react'
import {
  BufferGeometry,
  InstancedMesh,
  Material,
  Matrix4,
  MeshStandardMaterial,
  Vector3,
} from 'three'

import { useCompressedGltf, useDecorationSpawner, useLevelGeometry, type TerrainMask } from '@hooks'
import { useFrame, type ObjectMap } from '@react-three/fiber'
import { foliageWindVertBody, foliageWindVertHead } from '@shaders'
import {
  buildChunkedMeshes,
  coordHash,
  toLambert,
  type ChunkConfig,
  type DecorationConfig,
  type GeoInstance,
} from '@utils'
import { type GLTF } from 'three-stdlib'

type NodeMap = Record<
  string,
  { geometry: BufferGeometry; material: MeshStandardMaterial; matrix?: Matrix4 }
>
type UniformsArray = Array<Record<string, { value: unknown }>>

const VARIANTS = [
  // '/models/greenery/tree1-small_compressed.glb',
  '/models/greenery/tree2_compressed.glb',
  '/models/greenery/tree3_compressed.glb',
  '/models/greenery/tree4-tall_compressed.glb',
  '/models/greenery/tree5-tall_compressed.glb',
] as const

const NODE_PAIRS = [
  // ['Cylinder007', 'Cylinder007_1'],
  ['Cube015', 'Cube015_1'],
  ['Cylinder006', 'Cylinder006_1'],
  ['Cylinder003', 'Cylinder003_1'],
  ['Cylinder002', 'Cylinder002_1'],
] as const

const S = 400

// Creates a cloned material with wind shaders and captures
// the uniforms reference into the provided array.
function cloneWindMaterial(
  mat: MeshStandardMaterial,
  alphaTest: number,
  uniformsList: UniformsArray,
) {
  const m = toLambert(mat)
  m.transparent = false
  m.alphaTest = alphaTest
  m.depthWrite = true
  m.customProgramCacheKey = () => 'foliage-wind-v1'
  m.onBeforeCompile = shader => {
    shader.uniforms.uTime = { value: 0 }
    shader.uniforms.uWindSpeed = { value: 0.2 }
    shader.uniforms.uWindDirection = { value: [1.0, 0.3] }
    shader.uniforms.uWindDivergence = { value: 0.4 }
    shader.uniforms.uWindStrength = { value: 0.03 }

    // Store the reference so we can animate it in useFrame
    uniformsList.push(shader.uniforms)

    shader.vertexShader = shader.vertexShader
      .replace('#include <common>', `#include <common>\n${foliageWindVertHead}`)
      .replace('#include <begin_vertex>', `#include <begin_vertex>\n${foliageWindVertBody}`)
  }
  m.needsUpdate = true
  return m
}

// Pure function to build the meshes and collect uniforms.
function buildTreeFamily(
  gltfs: (GLTF & ObjectMap)[],
  nodePairs: readonly (readonly string[])[],
  instances: GeoInstance[][],
  chunkCfg: ChunkConfig,
): { meshes: InstancedMesh[]; uniforms: UniformsArray; clonedMaterials: Material[] } {
  const meshes: InstancedMesh[] = []
  const uniforms: UniformsArray = []
  const clonedMaterials: Material[] = []

  for (let vi = 0; vi < gltfs.length; vi++) {
    const insts = instances[vi]
    if (!insts || !insts.length) continue
    const nodes = gltfs[vi].nodes as unknown as NodeMap
    const [barkName, leavesName] = nodePairs[vi]
    const bark = nodes[barkName]
    const leaves = nodes[leavesName]

    if (!bark?.geometry) continue

    bark.geometry.computeBoundingBox()
    const offset = bark.geometry.boundingBox!.getCenter(new Vector3())

    // Bark (Static)
    meshes.push(...buildChunkedMeshes(bark.geometry, bark.material, insts, chunkCfg, offset, true))

    // Leaves (Animated)
    if (leaves?.geometry) {
      const leavesMat = cloneWindMaterial(leaves.material, 0.5, uniforms)
      clonedMaterials.push(leavesMat)
      meshes.push(...buildChunkedMeshes(leaves.geometry, leavesMat, insts, chunkCfg, offset, true))
    }
  }

  return { meshes, uniforms, clonedMaterials }
}

interface TreesProps {
  mask: TerrainMask
  full?: boolean
}

export function Trees({ mask, full = true }: TreesProps) {
  const { size } = useLevelGeometry()
  const frontZ = size.height / 2
  const rightX = (size.visibleWidth + size.centerOffset) / 2 + 1.5

  const gltfs = useCompressedGltf([...VARIANTS])
  const windUniformsRef = useRef<UniformsArray>([])

  const config = useMemo<DecorationConfig>(
    () => ({
      cellSize: 0.5,
      minTrackDist: 2.6,
      minSpacing: 1.5,
      density: 0.5,
      allow: (wx, wz, m) =>
        (wz < -frontZ || wx >= rightX) && (full || wz >= -frontZ) && m.sampleDarkGreen(wx, wz),
      score: (wx, wz) => coordHash(wx, wz, S),
    }),
    [frontZ, rightX, full],
  )

  const chunkCfg = useMemo<ChunkConfig>(
    () => ({ side: mask.side, centerOffset: mask.centerOffset, chunksX: 4, chunksZ: 4 }),
    [mask],
  )

  const instances = useDecorationSpawner(mask, config, VARIANTS.length, S, 0.7, 0.3)

  const { meshes, uniforms, clonedMaterials } = useMemo(
    () => buildTreeFamily(gltfs, NODE_PAIRS, instances, chunkCfg),
    [gltfs, instances, chunkCfg],
  )

  useEffect(() => {
    return () => {
      meshes.forEach(mesh => mesh.geometry.dispose())
      clonedMaterials.forEach(m => m.dispose())
    }
  }, [meshes, clonedMaterials])

  useLayoutEffect(() => {
    windUniformsRef.current = uniforms
  }, [uniforms])

  useFrame(({ clock }) => {
    const t = clock.getElapsedTime()
    const currentUniforms = windUniformsRef.current
    for (let i = 0; i < currentUniforms.length; i++) {
      currentUniforms[i].uTime.value = t
    }
  })

  return meshes.map((mesh, i) => <primitive key={i} object={mesh} />)
}
